/*****************************************************
Naam: DraggableModal.jsx
Versie: 4.0.0
CHANGELOG v4.0.0:
- FIX: ReactDOM.createPortal zodat fixed positioning correct werkt
- FIX: Initiële positie gecentreerd op scherm
- FIX: Resize werkt nu ook verticaal
- UI: X-knop rechtsboven, logo gecentreerd
******************************************************/

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Printer } from 'lucide-react';

const DraggableModal = ({ 
  title, isOpen, onClose, children, 
  isSettings, onAction, showSaveOnly, showCustomButtons,
  isFormEmpty, hasSelection, hasChanges = false,
  headerColor = 'default', width,
  initialPosition,
  hideDefaultButtons = false,
  showPrintButton = false, onPrint
}) => {
  const modalRef = useRef(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [position, setPosition] = useState(null); // null = nog niet berekend
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [modalSize, setModalSize] = useState({ width: null, height: null });

  useEffect(() => {
    const logo = localStorage.getItem('companyLogo');
    if (logo) setCompanyLogo(logo);
  }, []);

  // Centreer het window bij openen
  useEffect(() => {
    if (isOpen) {
      setModalSize({ width: null, height: null });
      if (initialPosition) {
        setPosition(initialPosition);
      } else {
        // Bereken gecentreerde positie
        const modalW = Math.min(window.innerWidth * 0.65, 1400);
        const x = Math.max(20, (window.innerWidth - modalW) / 2);
        const y = Math.max(20, window.innerHeight * 0.05);
        setPosition({ x, y });
      }
    }
  }, [isOpen, initialPosition]);

  // ── Drag ──────────────────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    if (e.target.closest('.modal-header') && !e.target.closest('button')) {
      e.preventDefault();
      setIsDragging(true);
      setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) setPosition({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => { setIsDragging(false); setIsResizing(false); }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // ── Resize ─────────────────────────────────────────────────────────────────
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = modalRef.current.getBoundingClientRect();
    setResizeStart({ x: e.clientX, y: e.clientY, w: rect.width, h: rect.height });
    setIsResizing(true);
  };

  const handleResizeMove = useCallback((e) => {
    if (isResizing) {
      const newW = Math.max(400, resizeStart.w + (e.clientX - resizeStart.x));
      const newH = Math.max(250, resizeStart.h + (e.clientY - resizeStart.y));
      setModalSize({ width: newW, height: newH });
    }
  }, [isResizing, resizeStart]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleResizeMove, handleMouseUp]);

  if (!isOpen || !position) return null;

  const btnBase = { border: 'none', fontSize: '13px', fontWeight: 600, padding: '8px 20px', color: '#fff', cursor: 'pointer' };

  const modalContent = (
    <div
      ref={modalRef}
      style={{
        position: 'fixed',
        border: '1px solid #cbd5e1',
        backgroundColor: '#f8fbfc',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: modalSize.width ? `${modalSize.width}px` : (width || (isSettings ? '900px' : '600px')),
        height: modalSize.height ? `${modalSize.height}px` : undefined,
        maxHeight: modalSize.height ? 'none' : '90vh',
        zIndex: 9999,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 12px 48px rgba(26,54,76,0.22), 0 2px 8px rgba(0,0,0,0.08)',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div
        className="modal-header cursor-move flex-shrink-0"
        style={{
          background: 'linear-gradient(135deg, #1a364c 0%, #2a4a64 100%)',
          padding: '11px 16px',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          userSelect: 'none',
        }}
      >
        <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#fff', margin: 0 }}>{title}</h2>
        {companyLogo
          ? <div style={{ background: 'rgba(255,255,255,0.1)', padding: '3px 12px' }}>
              <img src={companyLogo} alt="logo" style={{ height: 36, objectFit: 'contain' }} />
            </div>
          : <div />
        }
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none', color: '#fff',
              fontSize: '20px', fontWeight: 700,
              width: 30, height: 30, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            title="Sluiten"
          >×</button>
        </div>
      </div>

      {/* Cyan accent line */}
      <div style={{ height: '3px', background: '#c2e6ef', flexShrink: 0 }} />

      {/* Body */}
      <div style={{ overflow: 'auto', flex: 1, padding: '20px' }}>
        {children}
      </div>

      {/* Footer */}
      <div style={{ borderTop: '2px solid #c2e6ef', backgroundColor: 'rgba(194,230,239,0.13)', padding: '10px 20px', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
        {isSettings && onAction && (
          <>
            {showSaveOnly ? (
              <button onClick={() => onAction('save')} style={{ ...btnBase, backgroundColor: '#059669' }}>Opslaan</button>
            ) : showCustomButtons ? (
              <>
                <button onClick={() => onAction('add')} disabled={hasSelection} style={{ ...btnBase, backgroundColor: !hasSelection ? '#059669' : '#e2e8f0', color: !hasSelection ? '#fff' : '#94a3b8' }}>Toevoegen</button>
                <button onClick={() => onAction('edit')} disabled={!hasSelection || !hasChanges} style={{ ...btnBase, backgroundColor: hasSelection && hasChanges ? '#2563eb' : '#e2e8f0', color: hasSelection && hasChanges ? '#fff' : '#94a3b8' }}>Wijzigen</button>
                <button onClick={() => onAction('delete')} disabled={!hasSelection} style={{ ...btnBase, backgroundColor: hasSelection ? '#d97706' : '#e2e8f0', color: hasSelection ? '#fff' : '#94a3b8' }}>Verwijderen</button>
              </>
            ) : (
              <>
                <button onClick={() => onAction('add')} style={{ ...btnBase, backgroundColor: '#059669' }}>Toevoegen</button>
                <button onClick={() => onAction('edit')} style={{ ...btnBase, backgroundColor: '#2563eb' }}>Wijzigen</button>
                <button onClick={() => onAction('delete')} style={{ ...btnBase, backgroundColor: '#d97706' }}>Verwijderen</button>
              </>
            )}
          </>
        )}
        {showPrintButton && onPrint && (
          <button onClick={onPrint} style={{ ...btnBase, backgroundColor: '#1a364c', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Printer size={14} /> Afdrukken
          </button>
        )}
        {!hideDefaultButtons && (
          <button onClick={onClose} style={{ ...btnBase, backgroundColor: '#e11d48' }}>Afsluiten</button>
        )}
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        style={{
          position: 'absolute', right: 0, bottom: 0,
          width: 20, height: 20, cursor: 'nwse-resize',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
          padding: 3, zIndex: 10,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12">
          <path d="M11 1 L1 11 M11 5 L5 11 M11 9 L9 11" stroke="rgba(26,54,76,0.35)" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default React.memo(DraggableModal);
